import React, { useContext, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Col, Row, Segment } from 'tea-component';
import { ANALYTICS_CONFIG, SHOW_PAGE_CARD_LIST } from '@/pages/home/pages/studio/config';
import MetricCard, { MetricCardTitle } from '@/pages/home/pages/studio/components/MetricCard';
import { PageLogMes } from '@home/utils/buriedPointCfg';
import { buriedPointGame } from '@home/utils/buriedPoint';
import { StudioContext } from '@/pages/home/pages/studio/context';
import StudioState from '@/pages/home/pages/studio/components/StudioState';
import InGameNoPermissionState from '@/pages/home/pages/studio/inGameAnalytics/components/InGameNoPermissionState';
import { TAccessList } from '@/pages/home/pages/studio/type';
import { getStudioCard , getCardJump } from '@home/services/studio';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import './index.scss';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';

const InGameAnalytics = (props:any) => {
  const {
    tabList,
    customConfig,
    activeTab,
    setActiveTab,
    defaultTheme = true,
    // 进入页面指标请求初始日期偏移量，默认 T-1
    daySub = 1,
    // 是否取最新有数日期，默认 true（与历史行为一致）
    isLatestDate = true,
    // 指定经分鉴权路径时，isExist/isAccess 都只看该 path；未指定则走历史默认逻辑
    accessPath,
  } = props || {};
  const { currentGame, menuPermission } = useContext(StudioContext);
  const { game_code: orgGameCode, pc_path_access_list: accessList, specialGameCode } = currentGame || {};
  const [keyMetricsDataList, setKeyMetricDataList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  // 前端映射的gamecode优先
  const gameCode = specialGameCode || orgGameCode;
  let config;
  if (customConfig?.length > 0) {
    config = customConfig;
  } else if (SHOW_PAGE_CARD_LIST?.[gameCode]?.length > 0) {
    config = SHOW_PAGE_CARD_LIST[gameCode];
  } else {
    config = ANALYTICS_CONFIG;
  }


  
  useEffect(() => {
    if (!defaultTheme) {
    // 设置全局样式
      const element = document.querySelector('.dbh-studio-context');
      if (element && menuPermission === true) {
        (element as HTMLElement).style.backgroundColor = '#ebecf1';
      }
      return () => {
      // 去掉全局样式
        if (element) {
          (element as HTMLElement).style.backgroundColor = '#fff';
        }
      };
    }
  }, [menuPermission]);

  // 页面打点
  useEffect(() => {
    if (!gameCode) return;
    buriedPointGame({gameName: currentGame?.game_name, ...PageLogMes.studioInGameAnalytics}, gameCode, 'game_code');
  }, [gameCode, currentGame]);
  const controller = new AbortController();

  // 是否有gameCode 判断InGameAnalytics查看权限
  // 菜单is_visit字段判断左侧菜单访问权限，pc_path_access_list对应经分权限（is_exist是否存在页面，is_visit是否有页面权限）
  // accessPath 有值时仅校验该路径；否则默认：exist=overview|sales，access=sales
  const { isExist, isAccess } = useMemo(() => {
    if (accessPath) {
      const target = accessList?.find((v: TAccessList) => v?.path === accessPath) || {};
      const isExist = !!(target?.is_exist && gameCode);
      const isAccess = !!target?.is_visit;
      return {
        isExist: specialGameCode ? true : isExist,
        isAccess: specialGameCode ? true : isAccess,
      };
    }
    const overviewDaily = accessList?.find((v: TAccessList) => v?.path === '/overview/daily') || {};
    const salesOverview = accessList?.find((v: TAccessList) => v?.path === '/monetization/salesOverview') || {};
    const isOveviewExist = overviewDaily?.is_exist || false;
    const isSalesOverviewExist = salesOverview?.is_exist || false;
    const isSalesOverviewVisit = salesOverview?.is_visit || false;
    // 是否存在页面
    const isExist = (isOveviewExist || isSalesOverviewExist) && gameCode;
    // 是否有salesoverview页面权限
    const isAccess = isSalesOverviewVisit;
    return { isExist: specialGameCode ? true : isExist, isAccess: specialGameCode ? true : isAccess };
  }, [accessList, accessPath, gameCode, specialGameCode]);

  // 指标卡片接口请求
  useEffect(() => {
    const { studio_code: studioCode } = currentGame || {};
    // const config = gameCode === 'dl2' ? DL2_ANALYTICS_CONFIG : ANALYTICS_CONFIG;
    const metricList = config?.find(v => v?.cardType === 'metric')?.list || [];
    if (!metricList?.length || !gameCode || !isAccess) return;
    setLoading(true);
    const time = moment().subtract(daySub, 'days')?.format('YYYY-MM-DD');
    const params = {
      studio_code: studioCode,
      game_code: gameCode,
      period: "daily",
      start_time: time,
      end_time: time,
      is_confirm: false,
      is_latest_date: isLatestDate,
    };
    const list = metricList?.map((item) => getStudioCard({
      ...params, metrics: [item?.metricKey],
      trend_metrics: [item?.metricKey]
    }, { signal: controller.signal }));
    Promise.all(list).then((res: any) => {
      setKeyMetricDataList(res);
    }).catch(() => {
      setKeyMetricDataList([]);
    }).finally(() => {
      setLoading(false);
    });
    return () => {
      controller.abort('页面离开，cancel调用');
      // 销毁后更新为初始态
      setLoading(true);
    };
  }, [currentGame, isAccess, daySub, isLatestDate]);

  const [jumpConfigs, setJumpConfigs] = useState<Record<string, string>>({});

  useEffect(() => {
    isAccess && getCardJump({
      game_code: gameCode,
      module_type: 'inGameAnalytics',
    }).then((res) => {
      const urlMap = res.reduce((acc: Record<string, string>, item: any) => {
        if (item.url === '') {
          return acc;
        }
        if(item.module_key) {
          item.url === '-' ? acc[item.module_key] = 'notification' : acc[item.module_key] = item.url;
        }
        if(item.header_module_key) {
          item.url === '-' ? acc[item.header_module_key] = 'notification' : acc[item.header_module_key] = item.url;
        }
        return acc;
      }, {...jumpConfigs});
      setJumpConfigs(urlMap);
    }).catch(() => {});
  },[currentGame, isAccess]);

  const processedConfig = config?.map(item => {
    const { list, ...rest } = item || {};
    
    const processedList = list?.map(citem => {
      const result: any = { ...citem };
      if (citem.module_key && jumpConfigs[citem.module_key]) {
        result.jumpUrl = jumpConfigs[citem.module_key];
      }
      if (citem.header_module_key && jumpConfigs[citem.header_module_key]) {
        result.headerJumpUrl = jumpConfigs[citem.header_module_key];
      }
  
      return result;
    });
  
    return { ...rest, list: processedList };
  });

  const segmentOptions = useMemo(() => (
    (tabList || []).map((tab: any) => ({
      value: tab.id,
      text: isEn ? tab.tabName : tab.tabNameCn,
    }))
  ), [tabList, isEn]);

  if (menuPermission === false) {
    return <InGameNoPermissionState />;
  }

  if (!isExist) {
    return <StudioState />;
  }

  if (!isAccess) {
    return <InGameNoPermissionState />;
  }

  return (
    <>
      {tabList && <Segment
        style={{"marginBottom": "20px"}}
        value={activeTab}
        onChange={(value) => setActiveTab(value)}
        options={segmentOptions}
      />}
      <div className='dbh-in-game-analytics dbh-old-in-game-analytics' key={gameCode}>
        {processedConfig?.map((item, index) => {
          const { className = '', cardType, gap = 20, list } = item || {};
          return <div className={className} key={index}>
            {cardType === 'metric' && <MetricCardTitle metric={item} gameCode={gameCode} />}
            <Abnormal
              isLoading={cardType === 'metric' ? loading : false}
              isError={false}
              style={{ height: '160px', border: '1px solid #EBECF1', marginBottom: '20px' }}
              component={<Row gap={gap}>
                {list?.map((citem, index) => <Col span={citem?.span || 8} key={index}>
                  <MetricCard metric={citem} keyMetricsData={cardType === 'metric' ? keyMetricsDataList?.[index] : { card_list: [], trend_list: [], breakdown: null }} gameCode={gameCode} />
                </Col>)}
              </Row>} />
          </div>;
        })}
      </div>
    </>
  );
};

export default InGameAnalytics;